import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  Cpu,
  Database,
  FileSearch,
  Settings,
  Box,
  Network,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const SERVICES = [
  {
    title: "Agent AI",
    desc: "We design intelligent AI agents to streamline decisions and customer interactions.",
    hoverBorder: "rgba(147, 197, 253, 0.95)",
    hoverShadow: "4px 4px 4px 0px rgba(191, 219, 254, 0.85)",
    icon: (
      <img
        src="/images/what-we-do/icon-agentic-ai.png"
        alt="Agent AI"
        className="h-[55px] w-[55px] object-contain select-none"
      />
    ),
  },
  {
    title: "Legacy Data Migration",
    desc: "We migrate legacy data securely into modern, scalable platforms.",
    hoverBorder: "rgba(253, 186, 116, 0.95)",
    hoverShadow: "4px 4px 4px 0px rgba(255, 214, 186, 0.85)",
    icon: (
      <svg
        className="h-[55px] w-[55px] text-[#F97316]"
        viewBox="0 0 32 32"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <ellipse cx="16" cy="9" rx="10" ry="4" />
        <path d="M6 9v7c0 2.2 4.5 4 10 4s10-1.8 10-4V9" />
        <path d="M6 16v7c0 2.2 4.5 4 10 4s10-1.8 10-4v-7" />
      </svg>
    ),
  },
  {
    title: "Automation Audit",
    desc: "We identify automation opportunities and uncover workflow inefficiencies.",
    hoverBorder: "rgba(74, 222, 128, 0.95)",
    hoverShadow: "4px 4px 4px 0px rgba(127, 243, 169, 0.65)",
    icon: (
      <img
        src="/images/what-we-do/icon-automation-audit.png"
        alt="Automation Audit"
        className="h-[55px] w-[55px] object-contain select-none"
      />
    ),
  },
  {
    title: "Automated Workflows",
    desc: "We build custom automated workflows tailored to your business needs.",
    hoverBorder: "rgba(216, 180, 254, 0.95)",
    hoverShadow: "4px 4px 4px 0px rgba(236, 191, 255, 0.65)",
    icon: (
      <img
        src="/images/what-we-do/icon-automated-workflows.png"
        alt="Automated Workflows"
        className="h-[55px] w-[55px] object-contain select-none"
      />
    ),
  },
  {
    title: "Digital Workers",
    desc: "Digital workers that seamlessly connect services, systems, and customers.",
    hoverBorder: "rgba(244, 114, 208, 0.95)",
    hoverShadow: "4px 4px 4px 0px rgba(255, 182, 248, 0.65)",
    icon: (
      <svg
        className="h-[55px] w-[55px] text-[#C026D3]"
        viewBox="0 0 32 32"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M16 4l10 5.8v11.6L16 27.2 6 21.4V9.8L16 4z" />
        <path d="M16 15.6V27.2M16 15.6L26 9.8M16 15.6L6 9.8" />
      </svg>
    ),
  },
  {
    title: "ERP Implementation",
    desc: "We implement ERP solutions that unify core business operations.",
    hoverBorder: "rgba(250, 204, 21, 0.95)",
    hoverShadow: "4px 4px 4px 0px rgba(255, 223, 97, 0.65)",
    icon: (
      <img
        src="/images/what-we-do/icon-erp-implementations.png"
        alt="ERP Implementation"
        className="h-[55px] w-[55px] object-contain select-none"
      />
    ),
  },
];

const PRODUCTS = [
  {
    title: "Ek Rewards",
    desc: (
      <>
        Boost customer loyalty, encourage<br />
        repeat purchases, and drive<br />
        sustainable business growth.
      </>
    ),
    icon: "/images/what-we-do/icon-ek-rewards.png",
  },
  {
    title: "XP Skills",
    desc: (
      <>
        A modern learning platform to help<br />
        individuals build in-demand skills<br />
        and advance their careers.
      </>
    ),
    icon: "/images/what-we-do/icon-xp-skills.png",
  },
  {
    title: "Simpel ERP",
    desc: (
      <>
        Simplify finance, HR, and business<br />
        operations with one integrated<br />
        cloud-based platform.
      </>
    ),
    icon: "/images/what-we-do/icon-simpel-erp.png",
  },
  {
    title: "Time Tracker",
    desc: (
      <>
        Track productivity, manage<br />
        projects, and streamline workforce<br />
        operations with ease.
      </>
    ),
    icon: "/images/what-we-do/icon-time-tracker.png",
  },
];

const AUTOMATION_STACKS = [
  {
    title: "Manufacturing automation Stacks",
    desc: "Unify shop floor operations, IoT systems, and machinery data with intelligent automation workflows.",
    image: "/images/what-we-do/manufacturing-stack.png",
    icon: (
      <svg
        className="w-[30px] h-[30px] text-[#0284C7]"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1Z" />
      </svg>
    ),
  },
  {
    title: "Sales automation Stacks",
    desc: "Unify sales pipelines, CRM data, and multi-channel customer workflows to accelerate revenue growth.",
    image: "/images/what-we-do/sales-stack.png",
    icon: (
      <svg
        className="w-[28px] h-[28px] text-[#0284C7]"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M18 20V10" />
        <path d="M12 20V4" />
        <path d="M6 20v-6" />
      </svg>
    ),
  },
];

const WhatWeDo = () => {
  const navigate = useNavigate();
  const [hoveredServiceIndex, setHoveredServiceIndex] = useState(null);

  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        }
      }, 150);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash]);

  const handleTalkToTeam = () => {
    window.dispatchEvent(new CustomEvent("open-aura-agent"));
  };

  return (
    <div className="min-h-screen bg-[#010710] text-white selection:bg-[#00D2EE] selection:text-[#010710] overflow-x-hidden">
      <Navbar />

      <main>
        {/* Hero Section - exact Figma 1440 x 612 framing below navbar */}
        <section
          className="relative w-full overflow-hidden bg-[#001026] pt-[76px] sm:pt-[80px]"
        >
          {/* Background Graphic - seamless wide 2560x612 image centered and starting below navbar */}
          <div className="absolute inset-0 top-[76px] sm:top-[80px] w-full h-[540px] sm:h-[580px] lg:h-[612px] z-0">
            <img
              src="/images/what-we-do/what-we-do-hero-wide.png"
              alt="What We Do - Simpel AI"
              className="w-full h-full object-cover object-center select-none"
            />
          </div>

          <div className="relative mx-auto w-full max-w-[1440px] h-[540px] sm:h-[580px] lg:h-[612px] flex items-center z-10">
            {/* Left Content Overlay - positioned identically to Figma reference PNG */}
            <div className="relative z-10 px-6 sm:px-10 lg:px-[64px] max-w-[500px]">
              {/* Eyebrow */}
              <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#7E98B8]">
                OUR CAPABILITIES
              </p>

              {/* Main Headline - guaranteed 1 line */}
              <h1 className="mt-3 text-[36px] sm:text-[44px] lg:text-[50px] xl:text-[54px] font-extrabold leading-tight tracking-tight text-white whitespace-nowrap">
                <span>What </span>
                <span className="text-[#00D2EE]">We Do</span>
              </h1>

              {/* Subtitle */}
              <p className="mt-3.5 text-[13.5px] sm:text-[14px] lg:text-[14.5px] font-normal leading-[22px] sm:leading-[24px] text-[#9FB3C8] max-w-[420px]">
                From core systems to customer experiences, we design and deliver AI-powered solutions that help businesses operate smarter, move faster and create lasting value.
              </p>

              {/* CTA Button */}
              <div className="mt-6 sm:mt-7">
                <button
                  onClick={handleTalkToTeam}
                  className="group inline-flex items-center gap-2.5 rounded-full bg-white px-7 py-3 text-[14px] sm:text-[14.5px] font-bold text-[#0B132B] shadow-[0_4px_24px_rgba(0,180,255,0.4)] transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_6px_30px_rgba(0,210,238,0.55)] active:scale-95 cursor-pointer"
                >
                  <span>Talk to Our Team</span>
                  <ArrowRight
                    className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                    strokeWidth={2.4}
                  />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section matching Screenshots 1, 2, 3, 4 */}
        <section
          id="services"
          className="relative w-full overflow-hidden"
          style={{
            backgroundColor: "rgba(243, 248, 255, 1)", // From Screenshot 3
          }}
        >
          {/* Background Dotted Wave Image from Screenshot 2 */}
          <div className="absolute bottom-0 left-0 pointer-events-none select-none z-0 max-w-[650px] lg:max-w-[760px] opacity-90">
            <img
              src="/images/what-we-do/services-bg-wave.png"
              alt=""
              aria-hidden="true"
              className="w-full h-auto object-contain object-bottom-left"
            />
          </div>

          {/* Section Container with exact Figma padding: Top 79px, Right 32px, Bottom 67px, Left 90px */}
          <div className="relative mx-auto w-full max-w-[1440px] z-10 pt-16 lg:pt-[79px] pb-16 lg:pb-[67px] pl-6 sm:pl-12 lg:pl-[90px] pr-6 sm:pr-8 lg:pr-[32px] flex flex-col lg:flex-row items-start justify-between gap-10 lg:gap-8">
            
            {/* Left Content Column */}
            <div className="w-full lg:w-[350px] xl:w-[370px] shrink-0 pt-1">
              <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#6B7F96]">
                SERVICES
              </p>
              <h2 className="mt-3.5 text-[32px] sm:text-[36px] lg:text-[40px] xl:text-[42px] font-extrabold leading-[1.15] tracking-tight text-[#0B132B]">
                Services that<br />
                move your<br />
                <span className="whitespace-nowrap">business forward.</span>
              </h2>
              <p className="mt-4 text-[13.5px] sm:text-[14px] leading-[23px] text-[#556987] max-w-[320px]">
                We design and implement solutions tailored to your business needs — from core systems to customer engagement and beyond.
              </p>
            </div>

            {/* Right Cards Grid matching Screenshot 4: Width Fill(311px), Height Fixed(232px), Radius 14px, Padding T:21, R:24, B:28, L:28 */}
            <div className="flex-1 w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 xl:gap-[18px]">
              {SERVICES.map((srv, index) => {
                const isHovered = hoveredServiceIndex === index;
                const serviceSlug =
                  srv.title === "Agent AI"
                    ? "service-agentic-ai"
                    : srv.title === "ERP Implementation"
                    ? "service-erp-implementations"
                    : `service-${srv.title.toLowerCase().replace(/\s+/g, "-")}`;
                return (
                  <div
                    key={srv.title}
                    id={serviceSlug}
                    onClick={srv.title === "Agent AI" ? () => navigate("/services/agentic-ai") : undefined}
                    onMouseEnter={() => setHoveredServiceIndex(index)}
                    onMouseLeave={() => setHoveredServiceIndex(null)}
                    className={`group relative flex flex-col justify-between select-none scroll-mt-28 ${
                      srv.title === "Agent AI" ? "cursor-pointer" : "cursor-default"
                    }`}
                    style={{
                      backgroundColor: "rgba(249, 252, 255, 1)", // From Screenshot 4
                      borderRadius: "14px", // From Screenshot 4
                      border: isHovered
                        ? `1.5px solid ${srv.hoverBorder}`
                        : "1.5px solid rgba(226, 232, 240, 0.9)",
                      minHeight: "232px", // Fixed 232px from Screenshot 4
                      paddingTop: "21px", // From Screenshot 4
                      paddingRight: "24px", // From Screenshot 4
                      paddingBottom: "28px", // From Screenshot 4
                      paddingLeft: "28px", // From Screenshot 4
                      boxShadow: isHovered
                        ? srv.hoverShadow
                        : "0 4px 20px rgba(37, 99, 235, 0.04)",
                      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                    }}
                  >
                  {/* Top Row: Icon on left */}
                  <div className="flex items-center w-full">
                    {srv.icon}
                  </div>

                  {/* Bottom Text: Title and Description */}
                  <div className="mt-4">
                    <h3 className="text-[17px] font-bold text-[#0F172A] leading-tight">
                      {srv.title}
                    </h3>
                    <p className="mt-2 text-[12.5px] sm:text-[13px] leading-[19px] text-[#64748B]">
                      {srv.desc}
                    </p>
                  </div>
                  </div>
                );
              })}
            </div>

          </div>
        </section>

        {/* Products Section matching Screenshots 1, 2, 3, 4 */}
        <section
          id="products"
          className="relative w-full overflow-hidden"
          style={{
            backgroundColor: "rgba(1, 11, 22, 1)", // From Screenshot 4
          }}
        >
          {/* Top-Right Glowing Cyber Dotted Wave from Screenshot 2 */}
          <div className="absolute top-0 right-0 pointer-events-none select-none z-0 w-full sm:w-[70%] lg:w-[60%] max-w-[860px]">
            <img
              src="/images/what-we-do/products-bg-wave.png"
              alt=""
              aria-hidden="true"
              className="w-full h-auto object-contain object-top-right"
            />
          </div>

          {/* Section Container: exact Figma padding (Top 83px, Right 89px, Bottom 32px, Left 89px) */}
          <div className="relative mx-auto w-full max-w-[1440px] z-10 pt-16 sm:pt-20 lg:pt-[83px] pb-12 sm:pb-16 lg:pb-[32px] px-6 sm:px-12 lg:px-[89px] flex flex-col gap-8 sm:gap-10 lg:gap-[46px]">
            {/* Header Block */}
            <div className="w-full">
              <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#7E98B8]">
                PRODUCTS
              </p>
              <h2 className="mt-5 sm:mt-6 lg:mt-[28px] text-[26px] sm:text-[32px] md:text-[36px] lg:text-[40px] xl:text-[44px] font-extrabold leading-[1.15] tracking-tight text-white lg:whitespace-nowrap">
                Purpose-built products. Real business outcomes.
              </h2>
              <p className="mt-4 sm:mt-5 lg:mt-[24px] text-[13.5px] sm:text-[14px] lg:text-[14.5px] leading-[22px] sm:leading-[24px] text-[#8EA0B5] lg:whitespace-nowrap">
                Our products are designed to solve real business problems with speed, simplicity and scale.
              </p>
            </div>

            {/* Product Cards Grid: 4 columns, exact Figma card specs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 xl:gap-[20px] w-full">
              {PRODUCTS.map((prod) => (
                <div
                  key={prod.title}
                  className="relative flex flex-col justify-between cursor-default select-none"
                  style={{
                    background: "linear-gradient(180deg, rgba(6, 16, 27, 1) 0%, rgba(11, 26, 42, 1) 100%)", // From Screenshot 3
                    borderRadius: "14px", // From Screenshot 3
                    border: "1px solid rgba(23, 75, 104, 1)", // From Screenshot 3
                    minHeight: "251px", // Fixed 251px from Screenshot 3
                    padding: "30px", // 30px padding from Screenshot 3
                    gap: "14px", // Gap 14px from Screenshot 3
                  }}
                >
                  {/* Top Row: Icon badge on left */}
                  <div className="flex items-center w-full">
                    <img
                      src={prod.icon}
                      alt={prod.title}
                      className="w-[48px] h-[48px] rounded-[10px] object-cover select-none shadow-md"
                    />
                  </div>

                  {/* Bottom Text: Title and Description */}
                  <div>
                    <h3 className="text-[19px] sm:text-[20px] font-bold text-white tracking-tight leading-snug">
                      {prod.title}
                    </h3>
                    <p className="mt-2 text-[12.5px] sm:text-[13px] leading-[20px] text-[#8EA0B5]">
                      {prod.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Automation Stacks Section matching Screenshots 1, 2, 3, 4 */}
        <section
          id="stacks"
          className="relative w-full overflow-hidden"
          style={{
            backgroundColor: "rgba(243, 248, 255, 1)",
          }}
        >
          {/* Section Container matching Figma: Top ~82px, Bottom ~85px, Horizontal 89px */}
          <div className="relative mx-auto w-full max-w-[1440px] z-10 pt-16 sm:pt-20 lg:pt-[82px] pb-16 sm:pb-20 lg:pb-[85px] px-6 sm:px-12 lg:px-[89px]">
            {/* Header Block */}
            <div className="max-w-[700px]">
              {/* Eyebrow with cyan pill */}
              <div className="inline-flex items-center gap-2.5">
                <span className="w-[28px] h-[3px] bg-[#00D2EE] rounded-full inline-block" />
                <span className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#0F172A]">
                  AUTOMATION STACKS
                </span>
              </div>

              {/* Main Heading */}
              <h2 className="mt-4 text-[32px] sm:text-[40px] lg:text-[46px] xl:text-[50px] font-extrabold leading-[1.12] tracking-tight text-[#0B132B]">
                Automation stacks<br />built around your business.
              </h2>

              {/* Subtitle */}
              <p className="mt-4 text-[14px] sm:text-[15px] leading-[23px] sm:leading-[25px] text-[#556987] max-w-[580px]">
                From customer acquisition to operations, our automation stacks bring together people, processes and technology to drive efficiency.
              </p>
            </div>

            {/* Cards Grid: Fixed 608px width, Fixed 343px height, Radius 16px, Drop Shadow rgba(135,176,225,0.25) */}
            <div className="mt-10 sm:mt-12 lg:mt-[52px] grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 max-w-[1260px]">
              {AUTOMATION_STACKS.map((stack) => {
                const isManufacturing = stack.title.toLowerCase().includes("manufacturing");
                const isSales = stack.title.toLowerCase().includes("sales");
                const isClickable = isManufacturing || isSales;
                return (
                  <div
                    key={stack.title}
                    onClick={() => {
                      if (isManufacturing) {
                        navigate("/what-we-do/manufacturing-automation-stack");
                      } else if (isSales) {
                        navigate("/what-we-do/sales-automation-stack");
                      }
                    }}
                    className={`flex flex-col justify-between overflow-hidden w-full max-w-[608px] transition-all duration-300 ${
                      isClickable
                        ? "cursor-pointer hover:-translate-y-1.5 hover:shadow-[0_12px_32px_rgba(37,99,235,0.22)] group"
                        : "cursor-default select-none"
                    }`}
                    style={{
                      backgroundColor: "rgba(250, 252, 255, 1)", // From Screenshot 4
                      borderRadius: "16px", // From Screenshot 4
                      border: "1px solid rgba(226, 232, 240, 0.9)",
                      minHeight: "343px", // Fixed 343px from Screenshot 4
                      boxShadow: "0px 5px 18px 0px rgba(135, 176, 225, 0.25)", // From Screenshot 4
                    }}
                  >
                    {/* Top Image (static, no hover movement) */}
                    <div className="relative w-full h-[225px] sm:h-[238px] overflow-hidden bg-slate-100">
                      <img
                        src={stack.image}
                        alt={stack.title}
                        className="w-full h-full object-cover object-center"
                      />
                    </div>

                    {/* Bottom Info Bar with 3 sections: Icon | Text | Arrow */}
                    <div className="flex items-stretch border-t border-[#F1F5F9] min-h-[100px]">
                      {/* Left Icon Box */}
                      <div className="w-[74px] sm:w-[84px] flex items-center justify-center border-r border-[#E2E8F0]/80 shrink-0 bg-[#FAFCFF]">
                        {stack.icon}
                      </div>

                      {/* Middle Text Box */}
                      <div className="flex-1 px-5 sm:px-6 py-4 flex flex-col justify-center">
                        <h3 className="text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-tight">
                          {stack.title}
                        </h3>
                        <p className="mt-1.5 text-[12.5px] sm:text-[13px] text-[#64748B] leading-[19px]">
                          {stack.desc}
                        </p>
                      </div>

                      {/* Right Arrow Button Box */}
                      <div className="w-[68px] sm:w-[78px] flex items-center justify-center border-l border-[#E2E8F0]/80 shrink-0 bg-[#FAFCFF]">
                        <div
                          className={`flex h-[36px] w-[36px] items-center justify-center rounded-full border border-[#BFDBFE] text-[#2563EB] bg-white shadow-xs transition-all duration-300 ${
                            isClickable
                              ? "group-hover:bg-[#2563EB] group-hover:text-white group-hover:scale-110"
                              : "pointer-events-none"
                          }`}
                        >
                          <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Ready to Get Started / Let's build what's next together CTA Section */}
        {/* Matching Screenshots 1, 2, 3, 4: Width 1,440px x Height 486px */}
        <section
          className="relative w-full overflow-hidden bg-[#04101D]"
          style={{
            backgroundColor: "rgba(4, 16, 29, 1)", // From Screenshot 4
          }}
        >
          {/* 1440 x 486 Frame */}
          <div className="relative mx-auto w-full max-w-[1440px] h-auto min-h-[440px] sm:min-h-[460px] lg:h-[486px] flex items-center">
            {/* Background Graphic - Earth & City Skyline Banner from Screenshot 2 */}
            <div className="absolute inset-0 w-full h-full z-0">
              <img
                src="/images/what-we-do/cta-earth-banner.png"
                alt="Simpel AI - Let's build what's next together"
                className="w-full h-full object-cover object-right md:object-center select-none"
              />
              {/* Linear Gradient Overlay from Screenshot 4: rgba(4,16,29,1) to transparent */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(4, 16, 29, 0.98) 0%, rgba(4, 16, 29, 0.94) 40%, rgba(4, 16, 29, 0.75) 60%, rgba(7, 22, 38, 0.3) 78%, rgba(8, 42, 89, 0) 100%)",
                }}
              />
            </div>

            {/* Left Content Overlay */}
            <div className="relative z-10 px-6 sm:px-12 lg:px-[89px] py-14 sm:py-16 lg:py-0 max-w-[700px] lg:max-w-[760px]">
              {/* Eyebrow */}
              <p
                className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px]"
                style={{ color: "rgba(255, 255, 255, 1)" }}
              >
                READY TO GET STARTED?
              </p>

              {/* Main Heading - guaranteed 1 line */}
              <h2 className="mt-3.5 sm:mt-4 text-[28px] sm:text-[36px] lg:text-[42px] xl:text-[46px] font-extrabold leading-[1.14] tracking-tight text-white whitespace-nowrap">
                Let’s build what’s next, together.
              </h2>

              {/* Subtitle - exactly 3 lines */}
              <p className="mt-3.5 sm:mt-4 text-[13.5px] sm:text-[14px] lg:text-[14.5px] leading-[22px] sm:leading-[25px] text-[#CBD5E1] max-w-[610px]">
                Connect with our team to discover how Simpel.ai can transform your business, unlock new growth opportunities, streamline operations, and accelerate your digital transformation journey.
              </p>

              {/* CTA Button */}
              <div className="mt-7 sm:mt-8">
                <button
                  onClick={handleTalkToTeam}
                  className="group inline-flex items-center justify-center rounded-full border border-white/80 bg-transparent px-7 py-2.5 sm:py-3 text-[13.5px] sm:text-[14px] font-medium text-white transition-all duration-300 hover:bg-white hover:text-[#04101D] hover:shadow-[0_0_24px_rgba(255,255,255,0.4)] active:scale-95 cursor-pointer"
                >
                  Contact Us
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default WhatWeDo;
